import { action, observable } from "mobx";

import { PatientTab } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { AutofillDto } from "@libs/gateways/document/DocumentGateway.dtos.ts";
import { UserStorageKeys } from "@libs/gateways/user-experience/UserExperienceGateway.dtos.ts";

import { UserExperienceStore } from "./UserExperienceStore.ts";
import { UserHotkeysHelper } from "./UserHotkeysHelper.ts";

export class UserExperienceUi {
  constructor(private userExperience: UserExperienceStore) {
    this.userHotkeysHelper = new UserHotkeysHelper(userExperience);
  }
  public userHotkeysHelper: UserHotkeysHelper;

  @observable public showHelpDialog = false;

  @action public toggleHelpDialogVisibility = (visible?: boolean) => {
    this.showHelpDialog = visible || !this.showHelpDialog;
  };

  @observable
  editingAutoFill: AutofillDto | undefined;

  @action public setEditingAutoFill = (autofill: AutofillDto | undefined) => {
    this.editingAutoFill = autofill;
  };

  openTab = async (newTab: PatientTab, ignoreCache: boolean = false) => {
    const existingTabs = await this.getTabs(ignoreCache ?? false);
    const updatedTabs = [newTab, ...existingTabs];

    await this.saveTabs(updatedTabs).catch(error => {
      if (ignoreCache) throw error;
      return this.openTab(newTab, true);
    });
  };

  closeTab = async (
    filter: (tab: PatientTab) => boolean,
    ignoreCache: boolean = false
  ) => {
    const existingTabs = await this.getTabs(ignoreCache ?? false);
    const updatedTabs = existingTabs.filter(filter);

    await this.saveTabs(updatedTabs).catch(error => {
      if (ignoreCache) throw error;
      return this.closeTab(filter, true);
    });
  };

  private getTabs = async (ignoreCache: boolean) => {
    const userStorage = await this.userExperience.getUserStorage(
      UserStorageKeys.OpenPatientTabs,
      { ignoreCache }
    );

    return (userStorage?.jsonData || []) as PatientTab[];
  };

  private saveTabs = async (patientTabs: PatientTab[]) => {
    const userStorage = await this.userExperience.getUserStorage(
      UserStorageKeys.OpenPatientTabs
    );
    if (!userStorage) {
      await this.userExperience.addUserStorage(
        UserStorageKeys.OpenPatientTabs,
        {
          key: UserStorageKeys.OpenPatientTabs,
          userId: this.userExperience.root.core.userId,
          jsonData: patientTabs
        }
      );
    } else {
      await this.userExperience.updateUserStorage(
        UserStorageKeys.OpenPatientTabs,
        {
          id: userStorage.id,
          eTag: userStorage.eTag,
          key: UserStorageKeys.OpenPatientTabs,
          userId: userStorage.userId,
          jsonData: patientTabs
        }
      );
    }
  };
}
